import React from "react";
import { Helmet } from "react-helmet-async";

const SEO = ({
  title = "Playrep - Premium Gaming Platform | Playrep VIP, Playrep Pro, Playrep.me",
  description = "Experience the ultimate gaming platform with Playrep VIP, Playrep Pro, and Playrep.me. Premium gaming services with advanced features, revenue management, and seamless gameplay.",
  keywords = "playrep vip, playrep pro, playrep me, playrep, gaming platform, online gaming, revenue management, gaming dashboard, playrep vip login, playrep pro features, playrep.me services",
  image = "/logo512.png",
  url = "https://playrep.me",
  type = "website",
  siteName = "Playrep",
}) => {
  const fullTitle = title.includes("Playrep") 
    ? title 
    : `${title} | Playrep - Premium Gaming Platform`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Playrep",
    "alternateName": ["Playrep VIP", "Playrep Pro", "Playrep.me"],
    "description": description,
    "url": url,
    "applicationCategory": "GameApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "category": "Gaming Platform"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1000"
    },
    "keywords": keywords,
    "publisher": {
      "@type": "Organization",
      "name": "Playrep",
      "logo": {
        "@type": "ImageObject",
        "url": `${url}${image}`
      }
    }
  };

  const getCurrentPath = () => {
    if (typeof window !== 'undefined') {
      return window.location.pathname;
    }
    return '/';
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": url
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": title.split("|")[0].trim(),
        "item": `${url}${getCurrentPath()}`
      }
    ]
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Playrep",
    "alternateName": ["Playrep VIP", "Playrep Pro", "Playrep.me"],
    "url": url,
    "logo": `${url}${image}`,
    "sameAs": [
      // Add your social media links here when available
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": "English"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${url}${image}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={`${url}${image}`} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data - JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbStructuredData)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;

